import { ReactNode, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { Button } from '@atoms/buttons';
import { MultiSelect, MultiSelectProps } from './multiSelect';
import { renderCheckboxOption } from './selectOptions/renderCheckboxOption';
import { Stack } from '@mui/material';

export type FilterMultiSelectProps = Omit<MultiSelectProps, 'ref'> & {
  inlineLabel?: boolean;
  onApply?: (value: string[]) => void;
  onClear?: () => void;
  localizationSubset?: string;
};
export function FilterMultiSelect({
  options,
  inlineLabel,
  label,
  onChange,
  onApply,
  onClear,
  value,
  localizationSubset,
  ...props
}: FilterMultiSelectProps) {
  const intl = useIntl();
  const [internalValue, setInternalValue] = useState<string[]>(value);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  const fallbackOptionLabel = localizationSubset
    ? intl.formatMessage({ id: `${localizationSubset}.options.All` })
    : 'All';

  return (
    <MultiSelect
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      open={open}
      size="small"
      renderValue={(selected) => {
        const value = selected
          .map((v) => options.find((o) => o.value === v)?.label)
          .filter(Boolean)
          .join(', ');

        if (inlineLabel) {
          return <InlineLabel label={label} value={value.length === 0 ? fallbackOptionLabel : value} />;
        }
        return value;
      }}
      options={options}
      label={inlineLabel ? undefined : label}
      onChange={(v, e) => {
        setInternalValue(v);
        onChange?.(v, e);
      }}
      MenuProps={{
        sx: {
          '& .MuiMenu-list': {
            paddingBottom: 0,
            maxHeight: '580px',
          },
        },
      }}
      value={internalValue}
      controls={
        <Stack direction="row" justifyContent="flex-end" sx={{ width: '100%', minWidth: '340px' }} spacing={2}>
          <Box color="text.secondary">
            <Button
              rounded
              variant="text"
              color="inherit"
              onClick={() => {
                setOpen(false);
                onClear?.();
              }}
            >
              <FormattedMessage
                id={localizationSubset ? `${localizationSubset}.buttons.clear.label` : `common.clear`}
              />
            </Button>
          </Box>
          <Button
            rounded
            variant="contained"
            onClick={() => {
              setOpen(false);
              onApply?.(internalValue);
            }}
          >
            <FormattedMessage
              id={localizationSubset ? `${localizationSubset}.buttons.apply.label` : `common.applyFilters`}
            />
          </Button>
        </Stack>
      }
      renderOption={renderCheckboxOption}
      {...props}
    />
  );
}

type InlineLabelProps = {
  label: ReactNode;
  value: string;
};
function InlineLabel({ label, value }: InlineLabelProps) {
  return (
    <Stack direction="row">
      <Typography variant="body1" color="text.secondary" sx={{ flexShrink: 0 }}>
        {label}&nbsp;
      </Typography>
      <Typography variant="body1" color="text.primary" noWrap title={value}>
        {value}
      </Typography>
    </Stack>
  );
}
